import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { IonSearchbar } from '@ionic/angular';
import { IonPopover } from '@ionic/angular/standalone';

import { filter, isNil, orderBy, trim } from 'lodash-es';

import { SearchableSelectOption } from '../../../typings/searchable-select';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent)
    }
  ],
  selector: 'red-searchable-select',
  standalone: false,
  styleUrl: './searchable-select.component.scss',
  templateUrl: './searchable-select.component.html'
})
export class SearchableSelectComponent implements ControlValueAccessor, OnInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  @ViewChild('searchbar') searchbar!: IonSearchbar;

  @Input() options: SearchableSelectOption[] = [];

  @Input() allowCustomValue = false;

  @Input() placeholder = 'Select...';

  @Input() searchPlaceholder = 'Search...';

  @Input() noResultsText = 'No options found';

  @Input() customValuePrefix = 'Use';

  @Input() disabled = false;

  @Input() searchCaseInsensitive = true;

  @Input() displayWith: (option: SearchableSelectOption) => string = (option) => option.name;

  @ViewChild('popover') popover?: IonPopover;

  @Input()
  set errorText(value: string | undefined | null) {
    this._errorText = value ?? '';
    this.changeDetectorRef.markForCheck();
  }

  get errorText() {
    return this._errorText;
  }

  private _errorText = '';

  isOpen = false;
  searchTerm = '';

  value: string | null = null;

  private onChange: (value: string | null | undefined) => void = () => {};

  private onTouched: () => void = () => {};

  ngOnInit() {
    this.searchTerm = this.getDisplayValue(this.value);
  }

  get filteredOptions() {
    const term = this.normalize(this.searchTerm);

    if (!term) {
      return this.options;
    }

    return orderBy(
      filter(this.options, (option) => this.normalize(this.getDisplayValue(option))?.includes(term)),
      (option) => !this.normalize(this.getDisplayValue(option)).startsWith(term)
    );
  }

  get hasCustomValue() {
    if (!this.allowCustomValue) {
      return false;
    }

    const value = trim(this.searchTerm);

    if (!value) {
      return false;
    }

    return !this.options.some((option) => this.normalize(this.getDisplayValue(option)) === this.normalize(value));
  }

  get customValue() {
    return trim(this.searchTerm);
  }

  open() {
    if (this.disabled) {
      return;
    }

    this.isOpen = true;
    this.searchTerm = this.getDisplayValue(this.value);
  }

  close() {
    this.isOpen = false;
    this.onTouched();
  }

  onPopoverDismiss() {
    this.isOpen = false;

    if (!this.allowCustomValue) {
      this.searchTerm = this.getDisplayValue(this.value);
    }

    this.onTouched();
  }

  onSearchChange(event: CustomEvent) {
    this.searchTerm = event.detail.value ?? '';
  }

  selectOption(option: SearchableSelectOption) {
    this.value = option.value;
    this.searchTerm = this.getDisplayValue(option);

    this.onChange(option.value);
    this.onTouched();

    this.isOpen = false;
  }

  selectCustomValue() {
    if (!this.allowCustomValue) {
      return;
    }

    const value = trim(this.searchTerm);

    if (!value) {
      return;
    }

    this.value = value;

    this.onChange(value);
    this.onTouched();

    this.isOpen = false;
  }

  clear() {
    this.value = null;
    this.searchTerm = '';

    this.onChange(null);
    this.onTouched();
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    if (this.filteredOptions.length === 1) {
      this.selectOption(this.filteredOptions[0]);
      return;
    }

    if (this.allowCustomValue && this.customValue) {
      this.selectCustomValue();
    }
  }

  writeValue(value: string | null) {
    this.value = value;
    this.searchTerm = this.getDisplayValue(value);
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: SearchableSelectOption | string | null | undefined) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  getDisplayValue(value: SearchableSelectOption | string | null | undefined) {
    if (isNil(value)) {
      return '';
    } else if (typeof value === 'string') {
      const option = this.options.find((option) => option.value === value);
      return option ? this.displayWith(option) : value;
    } else {
      return this.displayWith(value);
    }
  }

  async onPopoverDidPresent(): Promise<void> {
    setTimeout(() => this.searchbar?.setFocus(), 200);
  }

  private normalize(value: string): string {
    return this.searchCaseInsensitive ? value?.toLowerCase() : value;
  }
}
